/**
 * Rules of the API báo cáo page (docs/specs/reporting-api.md §5, §12, §13), as plain functions with
 * no imports so node tests can load them: the kinds of data a place may receive and how they refer
 * to one another, the address list a key is limited to, key expiry choices, how a call and a sync
 * position read, and the Twenty 2.44 recipe's addresses and schedules.
 */

/** The kinds of data, in the Control Center's order (§12): Khách hàng, Tên miền, … */
export const REPORTING_SCOPES = [
  'reporting:customers',
  'reporting:domains',
  'reporting:orders',
  'reporting:invoices',
  'reporting:payments',
  'reporting:refunds',
  'reporting:balances',
  'reporting:stats',
] as const;

export type ScopeId = 'customers' | 'domains' | 'orders' | 'invoices' | 'payments' | 'refunds' | 'balances' | 'stats';

/** `reporting:customers` → `customers`. */
export function scopeId(scope: string): ScopeId {
  return scope.replace(/^reporting:/, '') as ScopeId;
}

/** The datasets (sync streams) of each kind of data. */
export const SCOPE_DATASETS: Readonly<Record<ScopeId, readonly string[]>> = {
  customers: ['customers'],
  domains: ['domains'],
  orders: ['orders', 'order_items'],
  invoices: ['invoices', 'invoice_lines'],
  payments: ['payments'],
  refunds: ['refunds'],
  balances: ['balances'],
  stats: ['daily_stats'],
};

/** The kind of data a dataset belongs to (`order_items` → `orders`). */
export function scopeOfDataset(dataset: string): ScopeId | null {
  for (const [scope, list] of Object.entries(SCOPE_DATASETS)) if (list.includes(dataset)) return scope as ScopeId;
  return null;
}

/** The datasets a place receives, in the Control Center's order. */
export function datasetsOf(scopes: readonly string[]): string[] {
  const ids = new Set(scopes.map(scopeId));
  return REPORTING_SCOPES.map(scopeId)
    .filter((s) => ids.has(s))
    .flatMap((s) => SCOPE_DATASETS[s]);
}

/**
 * The kinds of data each kind refers to (the catalogue's relations, crates/reporting/src/catalogue.rs).
 * A reference to a kind the place does not receive arrives as a plain code: the place cannot link it.
 */
export const SCOPE_PARENTS: Readonly<Record<ScopeId, readonly ScopeId[]>> = {
  customers: [],
  domains: ['customers'],
  orders: ['customers', 'domains', 'invoices'],
  invoices: ['customers', 'orders'],
  payments: ['customers', 'orders'],
  refunds: ['customers', 'orders', 'payments', 'invoices'],
  balances: ['customers'],
  stats: [],
};

/** For each ticked kind, the kinds it refers to that are not ticked. */
export function missingParents(selected: readonly string[]): { scope: ScopeId; missing: ScopeId[] }[] {
  const ids = new Set(selected.map(scopeId));
  return REPORTING_SCOPES.map(scopeId)
    .filter((s) => ids.has(s))
    .map((s) => ({ scope: s, missing: SCOPE_PARENTS[s].filter((p) => !ids.has(p)) }))
    .filter((x) => x.missing.length > 0);
}

/** Restarting `customers` or `invoices` of a place with contact data on sends that data again: an approval. */
export function resetNeedsApproval(contacts: boolean, dataset: string): boolean {
  return contacts && (dataset === 'customers' || dataset === 'invoices');
}

// ---------------------------------------------------------------------------
// Addresses a key may be used from

const IPV4 = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

function isIpv6(s: string): boolean {
  if (!/^[0-9a-f:.]+$/i.test(s) || !s.includes(':')) return false;
  const parts = s.split('::');
  if (parts.length > 2) return false;
  const groups = (p: string) => (p === '' ? [] : p.split(':'));
  const all = [...groups(parts[0] ?? ''), ...groups(parts[1] ?? '')];
  // An embedded IPv4 address counts for two groups.
  let count = 0;
  for (const [i, g] of all.entries()) {
    if (i === all.length - 1 && g.includes('.')) {
      if (!IPV4.test(g)) return false;
      count += 2;
    } else if (/^[0-9a-f]{1,4}$/i.test(g)) count += 1;
    else return false;
  }
  return parts.length === 2 ? count < 8 : count === 8;
}

/** One IP address or range (`203.0.113.10`, `203.0.113.0/24`, `2001:db8::/48`). */
export function isAddressOrRange(value: string): boolean {
  const [ip = '', prefix, extra] = value.trim().split('/');
  if (extra !== undefined) return false;
  const v4 = IPV4.test(ip);
  const v6 = !v4 && isIpv6(ip);
  if (!v4 && !v6) return false;
  if (prefix === undefined) return true;
  if (!/^\d{1,3}$/.test(prefix)) return false;
  return Number(prefix) <= (v4 ? 32 : 128);
}

/** The address list typed one per line (commas and spaces also separate). */
export function parseAddressList(text: string): { addresses: string[]; invalid: string[] } {
  const items = text
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const addresses: string[] = [];
  const invalid: string[] = [];
  for (const item of items) {
    if (!isAddressOrRange(item)) invalid.push(item);
    else if (!addresses.includes(item)) addresses.push(item);
  }
  return { addresses, invalid };
}

/** At most 20 addresses per key (the server's limit). */
export const MAX_ADDRESSES = 20;

// ---------------------------------------------------------------------------
// Keys

export const EXPIRY_CHOICES = ['90d', '6m', '1y', '2y'] as const;
export type ExpiryChoice = (typeof EXPIRY_CHOICES)[number];
const EXPIRY_DAYS: Record<ExpiryChoice, number> = { '90d': 90, '6m': 182, '1y': 365, '2y': 730 };

/** The expiry of a key issued now (at most two years: the server's limit is 731 days). */
export function expiryFor(choice: ExpiryChoice, now: number = Date.now()): string {
  return new Date(now + EXPIRY_DAYS[choice] * 86_400_000).toISOString();
}

export const ROTATION_OVERLAPS = [1, 7, 30] as const;

export type KeyState = 'ACTIVE' | 'EXPIRING' | 'SUSPENDED' | 'EXPOSED' | 'REVOKED' | 'EXPIRED';

/** How a key reads in the list. */
export function keyState(k: { status: string; suspended_at: string | null; suspended_reason: string | null; expiring_soon: boolean; expires_at: string | null }, now: number = Date.now()): KeyState {
  if (k.status === 'REVOKED') return 'REVOKED';
  if (k.status === 'EXPIRED' || (k.expires_at !== null && Date.parse(k.expires_at) <= now)) return 'EXPIRED';
  if (k.suspended_at) return k.suspended_reason === 'EXPOSED_IN_URL' ? 'EXPOSED' : 'SUSPENDED';
  return k.expiring_soon ? 'EXPIRING' : 'ACTIVE';
}

/** Why a key was suspended, as a label key (`BUDGET_EXCEEDED`, `NETWORK_NOT_ALLOWED`, `EXPOSED_IN_URL`, `OTHER`). */
export function suspendReason(reason: string | null): 'BUDGET_EXCEEDED' | 'NETWORK_NOT_ALLOWED' | 'EXPOSED_IN_URL' | 'OTHER' {
  return reason === 'BUDGET_EXCEEDED' || reason === 'NETWORK_NOT_ALLOWED' || reason === 'EXPOSED_IN_URL' ? reason : 'OTHER';
}

// ---------------------------------------------------------------------------
// Calls ("Lịch sử")

export const CALL_RESULTS = [
  'OK',
  'WRONG_SECRET',
  'REVOKED',
  'EXPIRED',
  'SUSPENDED',
  'NETWORK_NOT_ALLOWED',
  'KEY_WITHOUT_ADDRESSES',
  'DATASET_NOT_ALLOWED',
  'RATE_LIMITED',
  'UNAVAILABLE',
  'KEY_IN_ADDRESS',
  'CONSUMER_DISABLED',
  'INVALID',
  'OTHER',
] as const;
export type CallResult = (typeof CALL_RESULTS)[number];

/** What a call's outcome was, in the page's words (§12 "Kết quả"). */
export function callResult(c: { status: number; error_code: string | null; refusal_reason: string | null }): CallResult {
  if (c.status < 400) return 'OK';
  switch (c.refusal_reason) {
    case 'WRONG_SECRET':
    case 'REVOKED':
    case 'EXPIRED':
    case 'SUSPENDED':
    case 'NETWORK_NOT_ALLOWED':
    case 'KEY_WITHOUT_ADDRESSES':
    case 'DATASET_NOT_ALLOWED':
    case 'RATE_LIMITED':
    case 'UNAVAILABLE':
    case 'CONSUMER_DISABLED':
      return c.refusal_reason;
    case 'KEY_IN_QUERY':
    case 'EXPOSED_IN_URL':
      return 'KEY_IN_ADDRESS';
    case 'NO_STATE':
      return 'SUSPENDED';
    default:
      break;
  }
  if (c.status === 429 || c.error_code === 'RATE_LIMITED') return 'RATE_LIMITED';
  if (c.status === 503 || c.error_code === 'UNAVAILABLE') return 'UNAVAILABLE';
  if (c.status === 401) return 'WRONG_SECRET';
  if (c.status >= 400 && c.status < 500) return 'INVALID';
  return 'OTHER';
}

/** An error worth looking at: refused or failed, but not "too many calls" (that only means "next run"). */
export function isCallError(c: { status: number }): boolean {
  return c.status >= 400 && c.status !== 429;
}

// ---------------------------------------------------------------------------
// Sync position ("Cập nhật đến")

export type SyncProgress =
  | { kind: 'never' }
  | { kind: 'catchingUp'; rows: number; minutes: number | null }
  | { kind: 'lagging'; minutes: number }
  | { kind: 'upToDate' };

/** More rows left than one page: the place is still catching up. */
const BACKLOG_ROWS = 500;

export function syncProgress(s: { last_acknowledged_at: string | null; backlog_rows: number; catch_up_seconds: number | null; lag_seconds: number | null }): SyncProgress {
  if (!s.last_acknowledged_at) return { kind: 'never' };
  if (s.backlog_rows > BACKLOG_ROWS) {
    return { kind: 'catchingUp', rows: s.backlog_rows, minutes: s.catch_up_seconds === null ? null : Math.max(1, Math.round(s.catch_up_seconds / 60)) };
  }
  if (s.lag_seconds !== null && s.lag_seconds >= 60) return { kind: 'lagging', minutes: Math.round(s.lag_seconds / 60) };
  return { kind: 'upToDate' };
}

// ---------------------------------------------------------------------------
// Connecting Twenty (§13)

/** The address to read from: the connection document's server, made absolute on this console's host. */
export function resolveBaseUrl(server: string | undefined | null, origin: string): string {
  const url = (server ?? '').trim() || '/reporting/v1';
  if (/^https?:\/\//i.test(url)) return url.replace(/\/+$/, '');
  return `${origin.replace(/\/+$/, '')}/${url.replace(/^\/+/, '').replace(/\/+$/, '')}`;
}

/** Names of Twenty 2.44's own triggers and steps, as its workflow editor shows them. */
export const TWENTY_STEPS = {
  schedule: 'Schedule',
  webhook: 'Webhook',
  http: 'HTTP Request',
  iterator: 'Iterator',
  upsert: 'Upsert Record',
  ifElse: 'If/Else',
} as const;

/** Every dataset, in the Control Center's order. */
export const ALL_DATASETS: readonly string[] = REPORTING_SCOPES.flatMap((s) => SCOPE_DATASETS[scopeId(s)]);

/** Twenty objects per dataset (§13.2): the name to create under Settings › Data model. */
export const TWENTY_OBJECTS: Readonly<Record<string, string>> = {
  customers: 'tinoCustomer',
  domains: 'tinoDomain',
  orders: 'tinoOrder',
  order_items: 'tinoOrderItem',
  invoices: 'tinoInvoice',
  invoice_lines: 'tinoInvoiceLine',
  payments: 'tinoPayment',
  refunds: 'tinoRefund',
  balances: 'tinoBalance',
  daily_stats: 'tinoDailyStat',
};

/** Staggered schedules (§13.3), every 15 minutes: parents usually arrive first. */
export function twentySchedule(dataset: string): string {
  if (dataset === 'customers') return '0,15,30,45 * * * *';
  if (dataset === 'domains' || dataset === 'orders' || dataset === 'order_items' || dataset === 'invoices' || dataset === 'invoice_lines') return '5,20,35,50 * * * *';
  return '10,25,40,55 * * * *';
}

/**
 * The sample answer of the fetching step for one kind of data, from the connection document
 * (each kind has its own, so Twenty's variable picker offers that kind's own fields).
 */
export function sampleResponse(doc: unknown, dataset: string): unknown {
  const at = (v: unknown, k: string): unknown => (v !== null && typeof v === 'object' ? (v as Record<string, unknown>)[k] : undefined);
  let v: unknown = doc;
  for (const k of ['paths', '/sync/{dataset}', 'get', 'responses', '200', 'content', 'application/json', 'examples', dataset, 'value']) v = at(v, k);
  return v ?? null;
}

/** The page size to keep until Twenty's cost per page is measured (§13.4, §13.8). */
export const TWENTY_PAGE_SIZE = 200;

/** What to paste into Twenty's steps for one dataset; `step` is the name given to the fetching step. */
export function twentyRecipe(base: string, dataset: string, step: string) {
  return {
    fetch: `${base}/sync/${dataset}?limit=${TWENTY_PAGE_SIZE}`,
    ack: `${base}/sync/${dataset}/ack`,
    ackBody: `{"ack_token": "{{${step}.ack_token}}"}`,
    items: `{{${step}.items}}`,
    hasMore: `{{${step}.has_more}}`,
    matchId: '{{item.id}}',
    header: 'Authorization',
    headerValue: 'Bearer rpk_…',
    object: TWENTY_OBJECTS[dataset] ?? dataset,
    schedule: twentySchedule(dataset),
    status: `${base}/sync/${dataset}/status`,
    me: `${base}/me`,
  };
}
